/**
 * El asiento contable de una nómina, para mostrarlo en el detalle.
 *
 * Por defecto son dos asientos, el registro (devengo) y el pago, pero cada empresa
 * elige: solo uno de los dos, o los dos juntos en uno (`ENTRY_MODE_*`). Se leen de la
 * contabilidad tal como se escribieron —no se recalculan— porque lo que el usuario
 * tiene que ver es lo que hay.
 */

export const ENTRY_KIND_REGISTRATION = 'registration';
export const ENTRY_KIND_PAYMENT = 'payment';
/** Registro y pago en un solo asiento. */
export const ENTRY_KIND_COMBINED = 'combined';

// Qué asientos genera la empresa. Espejo de `ENTRY_MODE_*` en `nomina_entry.py`.
export const ENTRY_MODE_BOTH = 'both';
export const ENTRY_MODE_REGISTRATION_ONLY = 'registration_only';
export const ENTRY_MODE_PAYMENT_ONLY = 'payment_only';
export const ENTRY_MODE_COMBINED = 'combined';
export const DEFAULT_ENTRY_MODE = ENTRY_MODE_BOTH;

export interface EntryModeOption {
  value: string;
  text: string;
  hint: string;
}

/**
 * Opción «heredar»: vacío significa «usa el nivel de abajo». La cascada es la de las
 * cuentas —nómina → empleado → empresa—, y la resuelve el backend al generar.
 */
export const ENTRY_MODE_INHERIT = '';

// ---- Valores heredados (nómina → empleado → empresa) -------------------------
//
// Espejo de `SETTING_SOURCE_*` en `nomina_entry.py`. La opción «heredar» enseña el valor
// que se aplicaría, ya resuelto por el backend (`nomina.inherited_settings`), en vez de
// «lo que tenga el empleado o la empresa»: así el cliente lo ve sin abrir la ficha ni la
// configuración de la empresa. De dónde sale va en el tooltip.
export const SETTING_SOURCE_EMPLEADO = 'empleado';
export const SETTING_SOURCE_COMPANY = 'company';
export const SETTING_SOURCE_DEFAULT = 'default';

/** Un valor heredado y el nivel del que sale. */
export interface InheritedSetting {
  value: string;
  source: string;
}

/** Lo que hereda una nómina (o una ficha) si no fija nada. */
export interface InheritedSettings {
  entry_mode: InheritedSetting;
  gross_detail: InheritedSetting;
  deductions_detail: InheritedSetting;
  irpf_detail: InheritedSetting;
  ss_employee_detail: InheritedSetting;
  ss_employer_detail: InheritedSetting;
  ss_bodies_detail: InheritedSetting;
  accounting_method: InheritedSetting;
  /** Solo para un resumen: cómo va su líquido si la nómina no elige (`SUMMARY_NET_DETAIL_*`). */
  summary_net_detail: InheritedSetting;
  account_map: { [fieldName: string]: InheritedSetting };
}

/** Sin respuesta del servidor todavía: los valores por defecto, como si nadie fijara nada. */
export function createDefaultInheritedSettings(): InheritedSettings {
  return {
    entry_mode: { value: DEFAULT_ENTRY_MODE, source: SETTING_SOURCE_DEFAULT },
    gross_detail: { value: DEFAULT_GROSS_DETAIL, source: SETTING_SOURCE_DEFAULT },
    deductions_detail: { value: DEFAULT_GROSS_DETAIL, source: SETTING_SOURCE_DEFAULT },
    irpf_detail: { value: DEFAULT_GROSS_DETAIL, source: SETTING_SOURCE_DEFAULT },
    ss_employee_detail: { value: DEFAULT_GROSS_DETAIL, source: SETTING_SOURCE_DEFAULT },
    ss_employer_detail: { value: DEFAULT_GROSS_DETAIL, source: SETTING_SOURCE_DEFAULT },
    ss_bodies_detail: { value: DEFAULT_SS_BODIES_DETAIL, source: SETTING_SOURCE_DEFAULT },
    accounting_method: { value: DEFAULT_ACCOUNTING_METHOD, source: SETTING_SOURCE_DEFAULT },
    summary_net_detail: { value: DEFAULT_SUMMARY_NET_DETAIL, source: SETTING_SOURCE_DEFAULT },
    account_map: {},
  };
}

/** De dónde sale un valor heredado, en una frase para el tooltip. */
export function inheritedSourceLabel(source: string): string {
  if (source === SETTING_SOURCE_EMPLEADO) return 'Valor de la ficha del empleado.';
  if (source === SETTING_SOURCE_COMPANY) return 'Valor de la configuración de la empresa.';
  return 'Valor por defecto: ni el empleado ni la empresa tienen otro configurado.';
}

/**
 * La opción «heredar» con el valor que de verdad se aplica: «Registro y pago (dos
 * asientos) · del empleado». Elegirla deja el campo vacío, que es lo que hace que siga
 * al nivel de abajo si este cambia.
 */
function inheritOption(
  optionList: EntryModeOption[], inherited: InheritedSetting | null, fallbackText: string,
): EntryModeOption {
  const option = inherited ? optionList.find(item => item.value === inherited.value) : null;
  if (!option || !inherited) {
    return { value: ENTRY_MODE_INHERIT, text: fallbackText, hint: '' };
  }
  return {
    value: ENTRY_MODE_INHERIT,
    text: option.text + ' · ' + inheritedSourceShort(inherited.source),
    hint: option.hint + ' ' + inheritedSourceLabel(inherited.source),
  };
}

function inheritedSourceShort(source: string): string {
  if (source === SETTING_SOURCE_EMPLEADO) return 'del empleado';
  if (source === SETTING_SOURCE_COMPANY) return 'de la empresa';
  return 'por defecto';
}

/** Opciones del selector del EMPLEADO: la heredada de la empresa y las de la empresa. */
export function empleadoEntryModeOptionList(inherited: InheritedSetting | null = null): EntryModeOption[] {
  return [inheritOption(ENTRY_MODE_OPTION_LIST, inherited, 'Lo que tenga configurado la empresa')]
    .concat(ENTRY_MODE_OPTION_LIST);
}

/** Opciones del selector de la NÓMINA: la heredada del empleado o la empresa, y las demás. */
export function nominaEntryModeOptionList(inherited: InheritedSetting | null = null): EntryModeOption[] {
  return [inheritOption(ENTRY_MODE_OPTION_LIST, inherited, 'Lo que tenga el empleado o la empresa')]
    .concat(ENTRY_MODE_OPTION_LIST);
}

/** Las opciones del selector de la empresa, en el orden en que se enseñan. */
export const ENTRY_MODE_OPTION_LIST: EntryModeOption[] = [
  {
    value: ENTRY_MODE_BOTH,
    text: 'Registro y pago (dos asientos)',
    hint: 'El devengo y, aparte, el pago desde el banco. Es lo habitual.',
  },
  {
    value: ENTRY_MODE_REGISTRATION_ONLY,
    text: 'Solo el registro de la nómina',
    hint: 'Solo el devengo. El pago lo contabiliza usted al conciliar el banco.',
  },
  {
    value: ENTRY_MODE_PAYMENT_ONLY,
    text: 'Solo el pago de la nómina',
    hint: 'Solo la salida del banco. El devengo lo lleva por otra vía.',
  },
  {
    value: ENTRY_MODE_COMBINED,
    text: 'Registro y pago en un solo asiento',
    hint: 'Las líneas del registro y las del pago juntas en un único asiento.',
  },
];

// ---- Desglose del salario bruto ----------------------------------------------
//
// Espejo de `GROSS_DETAIL_*` en `nomina_entry.py`. Desglosado (por defecto): una línea
// por parte del bruto —640 lo que se paga en dinero, 6401 lo que se paga en especie—.
// En una línea: solo la 640 con el bruto entero. Misma cascada que el modo de asientos.
export const GROSS_DETAIL_SPLIT = 'split';
export const GROSS_DETAIL_SINGLE = 'single';
export const DEFAULT_GROSS_DETAIL = GROSS_DETAIL_SPLIT;
// La 476 con las dos SS en una línea: en una línea por defecto. Espejo de
// `DEFAULT_SS_BODIES_DETAIL`.
export const DEFAULT_SS_BODIES_DETAIL = GROSS_DETAIL_SINGLE;

/** Las opciones de la empresa, en el orden en que se enseñan. */
export const GROSS_DETAIL_OPTION_LIST: EntryModeOption[] = [
  {
    value: GROSS_DETAIL_SPLIT,
    text: 'Desglosado (una línea por cada parte)',
    hint: 'Sueldos y salarios (640) por lo que se paga en dinero y Retribución en especie '
      + '(6401) por lo que se paga en especie. Es lo habitual.',
  },
  {
    value: GROSS_DETAIL_SINGLE,
    text: 'En una sola línea',
    hint: 'Una única línea de Sueldos y salarios (640) con todo el salario bruto.',
  },
];

/** Opciones del selector del EMPLEADO: la heredada de la empresa y las de la empresa. */
export function empleadoGrossDetailOptionList(inherited: InheritedSetting | null = null): EntryModeOption[] {
  return [inheritOption(GROSS_DETAIL_OPTION_LIST, inherited, 'Lo que tenga configurado la empresa')]
    .concat(GROSS_DETAIL_OPTION_LIST);
}

/** Opciones del selector de la NÓMINA: la heredada del empleado o la empresa, y las demás. */
export function nominaGrossDetailOptionList(inherited: InheritedSetting | null = null): EntryModeOption[] {
  return [inheritOption(GROSS_DETAIL_OPTION_LIST, inherited, 'Lo que tenga el empleado o la empresa')]
    .concat(GROSS_DETAIL_OPTION_LIST);
}

/**
 * Los tipos de «otras deducciones», con su campo de importe y de cuenta. Espejo de
 * `DEDUCTION_TYPE_LIST` en `nomina_entry.py`. Los que no tienen cuenta propia en el PGC
 * usan la de otras deducciones.
 */
export interface DeductionType {
  amountField: string;
  accountField: string;
  label: string;
  /** Cuenta del PGC del tipo. '' = la de otras deducciones. */
  defaultAccount: string;
}

export const DEDUCTION_TYPE_LIST: DeductionType[] = [
  { amountField: 'deduction_in_kind_amount', accountField: 'deduction_in_kind_account', label: 'Descuento en especie', defaultAccount: '' },
  { amountField: 'deduction_advance_amount', accountField: 'deduction_advance_account', label: 'Anticipos', defaultAccount: '460' },
  { amountField: 'deduction_staff_loan_amount', accountField: 'deduction_staff_loan_account', label: 'Préstamos al personal', defaultAccount: '544' },
  { amountField: 'deduction_garnishment_amount', accountField: 'deduction_garnishment_account', label: 'Embargos', defaultAccount: '' },
  { amountField: 'deduction_union_fee_amount', accountField: 'deduction_union_fee_account', label: 'Cuota sindical', defaultAccount: '' },
];

/** Lo que no es de ningún tipo: el total menos los tipos (nunca negativo). */
export function deductionRemainder(nomina: any): number {
  const total = Number(nomina.other_deductions_amount) || 0;
  const typed = DEDUCTION_TYPE_LIST
    .reduce((sum, type) => sum + (Number(nomina[type.amountField]) || 0), 0);
  return Math.max(Math.round((total - typed) * 100) / 100, 0);
}

/** Las opciones del selector de la empresa para las otras deducciones. */
export const DEDUCTIONS_DETAIL_OPTION_LIST: EntryModeOption[] = [
  {
    value: GROSS_DETAIL_SPLIT,
    text: 'Desglosado (una línea por cada tipo)',
    hint: 'Una línea por cada tipo con importe (especie, anticipos, préstamos, embargos, '
      + 'cuota sindical), cada una en su cuenta, y otra con el resto. Es lo habitual.',
  },
  {
    value: GROSS_DETAIL_SINGLE,
    text: 'En una sola línea',
    hint: 'Una única línea de Otras deducciones con todo el importe.',
  },
];

// ---- Método de contabilización de la SS de la empresa ---------------------
//
// Espejo de `ACCOUNTING_METHOD_*` en `nomina_entry.py`. Completo: cada asiento lleva la SS
// de la empresa (642 y su cuota en la 476). Simplificado: no la lleva, porque la asesoría
// la registra en un asiento global a final de mes (lo habitual con A3).
export const ACCOUNTING_METHOD_FULL = 'full';
export const ACCOUNTING_METHOD_SIMPLIFIED = 'simplified';
export const DEFAULT_ACCOUNTING_METHOD = ACCOUNTING_METHOD_FULL;

export const ACCOUNTING_METHOD_OPTION_LIST: EntryModeOption[] = [
  {
    value: ACCOUNTING_METHOD_FULL,
    text: 'Completo (con la SS de la empresa)',
    hint: 'Cada asiento lleva el gasto de Seguridad Social de la empresa (642) y su cuota '
      + 'en Organismos de la Seguridad Social (476). Es lo habitual.',
  },
  {
    value: ACCOUNTING_METHOD_SIMPLIFIED,
    text: 'Simplificado (sin la SS de la empresa)',
    hint: 'El asiento de cada trabajador no lleva la SS de la empresa: la 476 recoge solo '
      + 'la cuota del trabajador. Para quien registra la SS de la empresa en un asiento '
      + 'global a final de mes.',
  },
];

// Cómo va el líquido de una nómina RESUMEN. Espejo de `SUMMARY_NET_DETAIL_*` del backend.
// Es lo que sale marcado en la casilla del popup de subida; la nómina se queda con lo que
// se eligió al subirla. Por defecto en una línea: así el resumen no necesita fichas.
export const SUMMARY_NET_DETAIL_PER_EMPLOYEE = 'per_employee';
export const SUMMARY_NET_DETAIL_SINGLE = 'single';
export const DEFAULT_SUMMARY_NET_DETAIL = SUMMARY_NET_DETAIL_SINGLE;

export const SUMMARY_NET_DETAIL_OPTION_LIST: EntryModeOption[] = [
  {
    value: SUMMARY_NET_DETAIL_PER_EMPLOYEE,
    text: 'Una línea por trabajador',
    hint: 'La 465 se parte en una línea por cada trabajador del resumen, con la cuenta de su '
      + 'ficha (o la de la empresa si no tiene). Cada trabajador tiene que estar dado de alta: '
      + 'los que falten se pueden crear desde la propia nómina.',
  },
  {
    value: SUMMARY_NET_DETAIL_SINGLE,
    text: 'Una sola línea con el total',
    hint: 'El líquido de toda la plantilla en una sola 465, con la cuenta de la empresa. No '
      + 'hace falta tener las fichas de los trabajadores.',
  },
];

/** Un concepto de la SS: su clave en `ss_*_line_map` y su nombre. */
export interface SsLine {
  key: string;
  label: string;
}

/** Las cotizaciones del trabajador. Espejo de `SS_EMPLOYEE_LINE_LIST`. */
export const SS_EMPLOYEE_LINE_LIST: SsLine[] = [
  { key: 'common_contingencies', label: 'Contingencias comunes' },
  { key: 'mei', label: 'MEI' },
  { key: 'unemployment', label: 'Desempleo' },
  { key: 'vocational_training', label: 'Formación profesional' },
  { key: 'overtime_force_majeure', label: 'Horas extra por fuerza mayor' },
  { key: 'overtime', label: 'Otras horas extra' },
  { key: 'solidarity', label: 'Cotización de solidaridad' },
];

/** Las cotizaciones de la empresa. Espejo de `SS_EMPLOYER_LINE_LIST`. */
export const SS_EMPLOYER_LINE_LIST: SsLine[] = [
  { key: 'common_contingencies', label: 'Contingencias comunes' },
  { key: 'mei', label: 'MEI' },
  { key: 'work_accidents', label: 'AT y EP' },
  { key: 'unemployment', label: 'Desempleo' },
  { key: 'vocational_training', label: 'Formación profesional' },
  { key: 'fogasa', label: 'FOGASA' },
  { key: 'overtime', label: 'Horas extra' },
  { key: 'solidarity', label: 'Cotización de solidaridad' },
];

/** Lo que no es de ningún concepto: el total menos sus conceptos. Va a una línea «Resto». */
export function ssLineRemainder(lineMap: { [key: string]: number } | undefined, total: number): number {
  const typed = Object.keys(lineMap || {})
    .reduce((sum, key) => sum + (Number((lineMap || {})[key]) || 0), 0);
  return Math.round(((Number(total) || 0) - typed) * 100) / 100;
}

/** Las opciones de la empresa para cada SS. */
export const SS_EMPLOYEE_DETAIL_OPTION_LIST: EntryModeOption[] = [
  {
    value: GROSS_DETAIL_SPLIT,
    text: 'Desglosado (una línea por cotización)',
    hint: 'Una línea de Organismos de la Seguridad Social (476) por cada cotización del '
      + 'trabajador: contingencias comunes, desempleo, formación… Es lo habitual.',
  },
  {
    value: GROSS_DETAIL_SINGLE,
    text: 'En una sola línea',
    hint: 'Una única línea con toda la cuota del trabajador.',
  },
];

export const SS_EMPLOYER_DETAIL_OPTION_LIST: EntryModeOption[] = [
  {
    value: GROSS_DETAIL_SPLIT,
    text: 'Desglosado (una línea por concepto)',
    hint: 'Una línea de Seguridad Social a cargo de la empresa (642) por cada concepto: '
      + 'contingencias comunes, desempleo, AT y EP, FOGASA… Es lo habitual.',
  },
  {
    value: GROSS_DETAIL_SINGLE,
    text: 'En una sola línea',
    hint: 'Una única línea con todo el coste de Seguridad Social de la empresa.',
  },
];

/**
 * Organismos de la SS (476) cuando las dos SS van en una línea: si alguna va desglosada,
 * la 476 ya sale separada. Espejo de `resolve_ss_bodies_detail`.
 */
export const SS_BODIES_DETAIL_OPTION_LIST: EntryModeOption[] = [
  {
    value: GROSS_DETAIL_SPLIT,
    text: 'Separada (cuota empresa y cuota trabajador)',
    hint: 'Dos líneas de Organismos de la Seguridad Social: la cuota de la empresa y la del '
      + 'trabajador, cada una en su cuenta. Solo cuenta cuando las dos SS van en una línea.',
  },
  {
    value: GROSS_DETAIL_SINGLE,
    text: 'En una sola línea',
    hint: 'Una única línea de Organismos de la Seguridad Social con las dos cuotas. Es lo '
      + 'habitual. Solo cuenta cuando las dos SS van en una línea.',
  },
];

/** Las opciones de un desglose con jerarquía, para el empleado o para la nómina. */
export function inheritedDetailOptionList(
  optionList: EntryModeOption[], inherited: InheritedSetting | null, isEmpleado: boolean,
): EntryModeOption[] {
  const fallback = isEmpleado ? 'Lo que tenga configurado la empresa' : 'Lo que tenga el empleado o la empresa';
  return [inheritOption(optionList, inherited, fallback)].concat(optionList);
}

/** Las opciones de la empresa para el IRPF. Espejo de `resolve_irpf_detail`. */
export const IRPF_DETAIL_OPTION_LIST: EntryModeOption[] = [
  {
    value: GROSS_DETAIL_SPLIT,
    text: 'Desglosado (dinero y especie)',
    hint: 'La retención de lo pagado en dinero y, aparte, el ingreso a cuenta de la '
      + 'retribución en especie (especie × tipo de IRPF). Es lo habitual.',
  },
  {
    value: GROSS_DETAIL_SINGLE,
    text: 'En una sola línea',
    hint: 'Una única línea de Retenciones de IRPF con todo el importe.',
  },
];

export function empleadoIrpfDetailOptionList(inherited: InheritedSetting | null = null): EntryModeOption[] {
  return [inheritOption(IRPF_DETAIL_OPTION_LIST, inherited, 'Lo que tenga configurado la empresa')]
    .concat(IRPF_DETAIL_OPTION_LIST);
}

export function nominaIrpfDetailOptionList(inherited: InheritedSetting | null = null): EntryModeOption[] {
  return [inheritOption(IRPF_DETAIL_OPTION_LIST, inherited, 'Lo que tenga el empleado o la empresa')]
    .concat(IRPF_DETAIL_OPTION_LIST);
}

/**
 * El IRPF de la especie (ingreso a cuenta): especie × tipo, sin pasar del total.
 * Espejo de `irpf_in_kind_amount`: la nómina no lo trae, se calcula.
 */
export function irpfInKindAmount(nomina: any): number {
  const inKind = Number(nomina.in_kind_amount) || 0;
  const rate = Number(nomina.irpf_rate) || 0;
  const total = Number(nomina.irpf_amount) || 0;
  if (!inKind || !rate || !total) return 0;
  return Math.min(Math.round(inKind * rate) / 100, total);
}

export function empleadoDeductionsDetailOptionList(inherited: InheritedSetting | null = null): EntryModeOption[] {
  return [inheritOption(DEDUCTIONS_DETAIL_OPTION_LIST, inherited, 'Lo que tenga configurado la empresa')]
    .concat(DEDUCTIONS_DETAIL_OPTION_LIST);
}

export function nominaDeductionsDetailOptionList(inherited: InheritedSetting | null = null): EntryModeOption[] {
  return [inheritOption(DEDUCTIONS_DETAIL_OPTION_LIST, inherited, 'Lo que tenga el empleado o la empresa')]
    .concat(DEDUCTIONS_DETAIL_OPTION_LIST);
}

/**
 * El desglose que se aplica a ESTA nómina: el suyo si lo fija, y si no el heredado. Es
 * lo que enseña el control pequeño de la fila «Salario bruto».
 */
export function effectiveGrossDetail(own: string, inherited: InheritedSetting | null): string {
  if (own === GROSS_DETAIL_SPLIT || own === GROSS_DETAIL_SINGLE) return own;
  return (inherited && inherited.value) || DEFAULT_GROSS_DETAIL;
}

export interface NominaEntryLine {
  account: string;
  description: string;
  debit: number;
  credit: number;
}

export interface NominaEntryMove {
  kind: string;
  description: string;
  total_debit: number;
  total_credit: number;
  is_balanced: boolean;
  line_list: NominaEntryLine[];
  /** Lo que el programa contable contestó de ESTE asiento. Vacío si aún no ha ido. */
  erp_state?: string;
  erp_move_number?: string;
  erp_error?: string;
}

export const ERP_ENTRY_STATE_DONE = 'done';
export const ERP_ENTRY_STATE_ERROR = 'error';
export const ERP_ENTRY_STATE_PENDING_SYNC = 'pending_sync';

/** Etiqueta de cada asiento, con el paso del ciclo que representa. */
export const ENTRY_KIND_LABEL_MAP: { [kind: string]: string } = {
  [ENTRY_KIND_REGISTRATION]: 'Registro de la nómina',
  [ENTRY_KIND_PAYMENT]: 'Pago de la nómina',
  [ENTRY_KIND_COMBINED]: 'Nómina y pago',
};

export const ENTRY_KIND_HINT_MAP: { [kind: string]: string } = {
  [ENTRY_KIND_REGISTRATION]: 'Devengo: el gasto y lo que se debe al trabajador y a la Seguridad Social.',
  [ENTRY_KIND_PAYMENT]: 'Salida del banco: se liquida lo que se debía al trabajador.',
  [ENTRY_KIND_COMBINED]: 'Registro y pago en un solo asiento: primero el devengo y después la salida del banco.',
};

export function getEntryKindLabel(kind: string): string {
  return ENTRY_KIND_LABEL_MAP[kind] || kind;
}

export function getEntryKindHint(kind: string): string {
  return ENTRY_KIND_HINT_MAP[kind] || '';
}

/**
 * Los dos asientos en su orden de lectura: primero se devenga, después se paga.
 *
 * El backend ya los devuelve así, pero el orden es parte de cómo se LEE un asiento
 * doble —y de que se entienda— así que se garantiza aquí en vez de confiarlo.
 */
export function sortMovesByKind(moveList: NominaEntryMove[]): NominaEntryMove[] {
  const order = [ENTRY_KIND_REGISTRATION, ENTRY_KIND_COMBINED, ENTRY_KIND_PAYMENT];
  return (moveList || []).slice().sort(
    (a, b) => order.indexOf(a.kind) - order.indexOf(b.kind),
  );
}

/** Si TODOS los asientos cuadran. Uno descuadrado hay que avisarlo, no esconderlo. */
export function areAllMovesBalanced(moveList: NominaEntryMove[]): boolean {
  return (moveList || []).every(move => move.is_balanced);
}

/**
 * Qué pasó con este asiento en el programa contable, para el chip de su cabecera.
 *
 * Se muestra POR ASIENTO porque A3 no puede borrar uno ya cargado: cuando el
 * registro entra y el pago es rechazado, la nómina dice «Errores» pero media
 * contabilidad del cliente ya tiene el asiento dentro. Sin esto, el usuario
 * reintentaría sin saber que va a duplicarlo.
 *
 * El número va en la etiqueta y no aparte: «Subido» a secas obliga a ir a buscarlo
 * al ERP, y es justo el dato con el que se comprueba que no está repetido.
 */
export function getEntryErpLabel(move: NominaEntryMove): string {
  const state = (move && move.erp_state) || '';
  if (state === ERP_ENTRY_STATE_DONE) {
    // La referencia que le dio el programa contable, que es con la que el contable lo
    // busca allí. NO se rellena con nada nuestro: llegó a decir «nº NOMINA-2026-09-…»
    // —nuestra referencia disfrazada de número de asiento— y después «documento
    // 9-28177627», un formato que deduje de una captura y que no existe. Enviar al
    // usuario a buscar algo inventado es peor que no decirle nada.
    //
    // Cuando el conector no la devuelve (modo DAT: el ERP numera al importar y se lo
    // calla), el chip dice «Subido» a secas hasta que la mande.
    const number = (move && move.erp_move_number) || '';
    return number ? 'Subido · asiento nº ' + number : 'Subido';
  }
  if (state === ERP_ENTRY_STATE_ERROR) return 'Rechazado';
  if (state === ERP_ENTRY_STATE_PENDING_SYNC) return 'En fichero, sin importar';
  return '';
}

export function getEntryErpColor(move: NominaEntryMove): string {
  const state = (move && move.erp_state) || '';
  if (state === ERP_ENTRY_STATE_DONE) return 'success';
  if (state === ERP_ENTRY_STATE_ERROR) return 'error';
  if (state === ERP_ENTRY_STATE_PENDING_SYNC) return 'warning';
  return '';
}

export function getEntryErpIcon(move: NominaEntryMove): string {
  const state = (move && move.erp_state) || '';
  if (state === ERP_ENTRY_STATE_DONE) return 'mdi-cloud-check-outline';
  if (state === ERP_ENTRY_STATE_ERROR) return 'mdi-cloud-alert';
  if (state === ERP_ENTRY_STATE_PENDING_SYNC) return 'mdi-file-clock-outline';
  return '';
}

/**
 * Si la subida fue a medias: uno dentro del ERP y otro no.
 *
 * Es el caso que hay que gritar, no susurrar. La nómina entera dice «Errores», y
 * eso por sí solo sugiere que no se subió nada — cuando la verdad es que en la
 * contabilidad del cliente ha quedado medio asiento que nadie va a quitar, porque
 * A3 no sabe borrarlos.
 */
export function isPartiallyUploaded(moveList: NominaEntryMove[]): boolean {
  const list = moveList || [];
  const uploaded = list.filter(move => move.erp_state === ERP_ENTRY_STATE_DONE);
  const failed = list.filter(move => move.erp_state === ERP_ENTRY_STATE_ERROR);
  return uploaded.length > 0 && failed.length > 0;
}

/** Los asientos que SÍ están en el ERP, por su nombre. Para nombrarlos en el aviso. */
export function uploadedMoveLabelList(moveList: NominaEntryMove[]): string[] {
  return (moveList || [])
    .filter(move => move.erp_state === ERP_ENTRY_STATE_DONE)
    .map(move => getEntryKindLabel(move.kind));
}

/**
 * Importe con formato español (1.600,00); vacío si la línea no lleva ese lado.
 *
 * Se formatea a mano en vez de con `toLocaleString('es-ES')` porque eso depende de
 * los locales que tenga instalado el entorno: en Node sin ICU completo devuelve
 * '1600,00' sin separador de miles, y en un asiento los miles son justo lo que hay
 * que poder leer de un vistazo.
 */
export function formatEntryAmount(amount: number): string {
  if (!amount) return '';
  const [whole, decimals] = Math.abs(amount).toFixed(2).split('.');
  const withThousands = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return (amount < 0 ? '-' : '') + withThousands + ',' + decimals;
}

/** Tolerancia de cuadre, la misma que el backend (`ENTRY_BALANCE_TOLERANCE`). */
export const ENTRY_BALANCE_TOLERANCE = 0.01;

/**
 * Recalcula los totales y el cuadre de un asiento editado.
 *
 * Hace falta porque los totales vienen del backend y **dejan de valer en cuanto el
 * usuario toca una línea**: sin esto el pie de la tabla seguiría mostrando el
 * asiento anterior mientras se edita.
 */
export function recalculateMoveTotals(move: NominaEntryMove): NominaEntryMove {
  const totalDebit = round2(sum(move.line_list.map(line => Number(line.debit) || 0)));
  const totalCredit = round2(sum(move.line_list.map(line => Number(line.credit) || 0)));
  return {
    ...move,
    total_debit: totalDebit,
    total_credit: totalCredit,
    is_balanced: Math.abs(totalDebit - totalCredit) <= ENTRY_BALANCE_TOLERANCE,
  };
}

/** De cuánto es el descuadre. "No cuadra" no basta: el importe dice dónde mirar. */
export function getMoveDifference(move: NominaEntryMove): number {
  return round2(Math.abs((move.total_debit || 0) - (move.total_credit || 0)));
}

/**
 * Errores del asiento editado. Vacío si se puede guardar.
 *
 * Repite las reglas del backend (`validate_editable_entry`) a propósito: allí es
 * donde se hacen valer —el bus se puede despachar desde cualquier sitio— y aquí se
 * ven al momento, sin esperar el viaje al servidor. Estilo *notification*: todos
 * los errores a la vez.
 */
// ---- Lo que admite el concepto en A3 ----------------------------------------
//
// Espejo de `erp_concept` / `latin1_text` del backend. A3 guarda 30 caracteres en el
// concepto del apunte, y el fichero DAT va en Latin-1: un carácter de fuera hace fallar
// la importación ENTERA, no solo esa línea.

export const ERP_CONCEPT_MAX_LENGTH = 30;

// Caracteres que A3 no acepta y lo que se pondría en su lugar. El backend los cambia
// igual antes de enviar; aquí se avisan para que el usuario vea lo que llegará.
const LATIN1_REPLACEMENT_MAP: { [char: string]: string } = {
  '€': 'EUR',
  '“': '"', '”': '"', '„': '"',
  '‘': "'", '’': "'", '‚': "'",
  '—': '-', '–': '-',
  '…': '...',
};

/** Los caracteres de `text` que A3 no admite (sin repetir), o vacío. */
export function nonLatin1CharList(text: string): string[] {
  const charList: string[] = [];
  Array.from(text || '').forEach((char) => {
    const isOutsideLatin1 = char.codePointAt(0) > 0xFF;
    if (isOutsideLatin1 && charList.indexOf(char) === -1) charList.push(char);
  });
  return charList;
}

/** El texto tal como llegará a A3: sin caracteres que no admite. */
export function latin1Text(text: string): string {
  return Array.from(text || '')
    .map(char => LATIN1_REPLACEMENT_MAP[char] || char)
    .join('')
    .split('')
    .filter(char => char.charCodeAt(0) <= 0xFF)
    .join('');
}

export function validateEntryMoves(moveList: NominaEntryMove[]): string[] {
  const errorList: string[] = [];
  (moveList || []).forEach((move) => {
    const label = move.description || move.kind;
    move.line_list.forEach((line) => {
      if (!(line.account || '').trim()) {
        errorList.push(label + ': hay una línea sin cuenta contable.');
      }
      const concept = line.description || '';
      if (concept.length > ERP_CONCEPT_MAX_LENGTH) {
        errorList.push(label + ': el concepto «' + concept + '» tiene ' + concept.length
          + ' caracteres; el programa contable solo guarda ' + ERP_CONCEPT_MAX_LENGTH + '.');
      }
      const badCharList = nonLatin1CharList(concept);
      if (badCharList.length) {
        errorList.push(label + ': el concepto «' + concept + '» lleva ' + badCharList.join(' ')
          + ', que el programa contable no admite. Se enviará como «' + latin1Text(concept) + '».');
      }
      if (line.debit && line.credit) {
        errorList.push(label + ': la cuenta ' + line.account
          + ' tiene importe en debe y haber a la vez.');
      } else if (!line.debit && !line.credit) {
        errorList.push(label + ': la cuenta ' + (line.account || 'sin cuenta')
          + ' no tiene importe.');
      }
    });
    const difference = getMoveDifference(move);
    if (difference > ENTRY_BALANCE_TOLERANCE) {
      errorList.push(label + ': el asiento no cuadra, debe y haber difieren en '
        + formatEntryAmount(difference) + '.');
    }
  });
  return errorList;
}

function sum(valueList: number[]): number {
  return valueList.reduce((total, value) => total + value, 0);
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

// ---- Asiento generado ↔ asiento de ahora (histórico de la nómina) -------------

/** Una línea del asiento, tal como se generó o tal como está ahora. */
export interface EntrySnapshotLine {
  account: string;
  description: string;
  debit: number;
  credit: number;
}

export interface EntrySnapshotMove {
  kind: string;
  description: string;
  line_list: EntrySnapshotLine[];
}

/** Qué le pasó a una línea entre el asiento generado y el de ahora. */
export const ENTRY_LINE_CHANGE_SAME = 'same';
export const ENTRY_LINE_CHANGE_EDITED = 'edited';
export const ENTRY_LINE_CHANGE_ADDED = 'added';
export const ENTRY_LINE_CHANGE_REMOVED = 'removed';

export interface EntryComparisonRow {
  kind: string;
  change: string;
  generated: EntrySnapshotLine | null;
  current: EntrySnapshotLine | null;
}

/**
 * Compara el asiento que generó el sistema con el que hay ahora, línea a línea.
 *
 * Es lo mismo que la pestaña «Lectura IA» hace con los datos: responde a «¿esto lo
 * puso el sistema o lo cambió alguien?». Las líneas se emparejan por posición DENTRO de
 * cada asiento, porque es como se editan (el usuario cambia la cuenta o el importe de una
 * fila); las que sobran a un lado son añadidas o quitadas.
 *
 * Vacío sin snapshot: una nómina generada antes de que existiera no tiene con qué
 * compararse, y decir "todo cambió" sería falso.
 */
export function buildEntryComparisonRowList(
  generatedMoveList: EntrySnapshotMove[],
  currentMoveList: EntrySnapshotMove[],
): EntryComparisonRow[] {
  if (!generatedMoveList || !generatedMoveList.length) return [];
  const kindList: string[] = [];
  generatedMoveList.concat(currentMoveList || []).forEach((move) => {
    if (kindList.indexOf(move.kind) === -1) kindList.push(move.kind);
  });
  const rowList: EntryComparisonRow[] = [];
  kindList.forEach((kind) => {
    const generated = _linesOfKind(generatedMoveList, kind);
    const current = _linesOfKind(currentMoveList || [], kind);
    const length = Math.max(generated.length, current.length);
    for (let index = 0; index < length; index += 1) {
      const generatedLine = generated[index] || null;
      const currentLine = current[index] || null;
      rowList.push({
        kind,
        change: _lineChange(generatedLine, currentLine),
        generated: generatedLine,
        current: currentLine,
      });
    }
  });
  return rowList;
}

export function countChangedEntryLines(rowList: EntryComparisonRow[]): number {
  return (rowList || []).filter(row => row.change !== ENTRY_LINE_CHANGE_SAME).length;
}

function _linesOfKind(moveList: EntrySnapshotMove[], kind: string): EntrySnapshotLine[] {
  const move = moveList.find(item => item.kind === kind);
  return move ? (move.line_list || []) : [];
}

function _lineChange(generated: EntrySnapshotLine | null, current: EntrySnapshotLine | null): string {
  if (generated && !current) return ENTRY_LINE_CHANGE_REMOVED;
  if (!generated && current) return ENTRY_LINE_CHANGE_ADDED;
  const isSame = (generated.account || '').trim() === (current.account || '').trim()
    && _sameAmount(generated.debit, current.debit)
    && _sameAmount(generated.credit, current.credit);
  return isSame ? ENTRY_LINE_CHANGE_SAME : ENTRY_LINE_CHANGE_EDITED;
}

/** Los importes se comparan como número y al céntimo: `2650` y `2650.00` son lo mismo. */
function _sameAmount(left: number, right: number): boolean {
  return Math.round((Number(left) || 0) * 100) === Math.round((Number(right) || 0) * 100);
}

// ---- Orden por defecto de las líneas (configuración de la empresa) ----------
//
// Espejo de `LINE_ROLE_*` / `DEFAULT_LINE_ROLE_ORDER` en `nomina_entry.py`. Se guarda en
// `configuration.nomina_line_order` y lo aplica el backend al generar el asiento.

export interface LineRoleOption {
  role: string;
  label: string;
}

/** Cada línea que genera el sistema, en el orden por defecto. Espejo del backend. */
export const DEFAULT_LINE_ROLE_OPTION_LIST: LineRoleOption[] = [
  { role: 'gross', label: 'Sueldos y salarios (640)' },
  { role: 'in_kind', label: 'Retribución en especie (6401)' },
  { role: 'ss_employer', label: 'SS a cargo de la empresa (642)' },
  { role: 'net', label: 'Remuneraciones pendientes de pago (465)' },
  { role: 'ss_bodies', label: 'Organismos de la Seguridad Social (476)' },
  { role: 'irpf', label: 'Retenciones de IRPF (4751)' },
  { role: 'irpf_in_kind', label: 'IRPF de la especie (4751)' },
  { role: 'other_deductions', label: 'Otras deducciones' },
  { role: 'bank', label: 'Banco del pago (572)' },
];

/**
 * El orden guardado, como lista de opciones para el selector: solo roles conocidos, sin
 * repetir, y los que falten al final (igual que `normalize_line_role_order`).
 */
export function lineRoleOptionListFromOrder(roleOrder: string[]): LineRoleOption[] {
  const known = DEFAULT_LINE_ROLE_OPTION_LIST;
  const result: LineRoleOption[] = [];
  (roleOrder || []).forEach((role) => {
    const option = known.find(item => item.role === role);
    if (option && result.indexOf(option) === -1) result.push(option);
  });
  return result.concat(known.filter(option => result.indexOf(option) === -1));
}
